import {
    CardGrid,
    ContentSection,
    LinkRow,
    PageFrame,
    PageHero,
    TruthStateGrid,
} from '../components/PageBlocks';
import { CLAIM_TRUTH_CLASSES, getPageConfig } from '../data/page-config.mjs';
import { ROADMAP_TARGET } from '../data/site.mjs';

const page = getPageConfig('/pricing');
const reserveLabel = 'Reserve with \u20AC1 deposit';

const PricingPage = () => {
    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="diagonal"
                numeral="VI"
                railLabel="Reservation / €1 deposit / Live"
                eyebrow="Reservation — Currently Open"
                title={page.h1}
                intro={`AvenBox is not yet a finished product. The reservation mechanism exists for serious early-access interest — not a public launch. A €1 deposit places you in a controlled pre-production position. Pricing and timing are directional, pointing toward ${ROADMAP_TARGET}.`}
                primaryCta={{ label: reserveLabel, to: '/contact' }}
                secondaryCta={{ label: 'Join Waitlist', to: '/contact', variant: 'secondary' }}
                proofStrip={[
                    'Reservation path is live now.',
                    'Public pricing is directional — not a locked specification.',
                    `Current launch target: ${ROADMAP_TARGET}.`,
                ]}
            />

            <ContentSection
                sectionId="reservation-tracks"
                eyebrow="Reservation"
                title="One unit. One track."
                intro="AvenBox ships as a single configuration. The reservation operates through a €1 deposit mechanism. Pricing shown reflects the current public direction — not a locked specification."
                borderTop={false}
            >
                <div className="ed-price">
                    <div className="ed-price__head">
                        <span className="ed-price__eyebrow">AvenBox — single unit</span>
                        <span className="truth-pill truth-pill--estimated">
                            {CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL}
                        </span>
                    </div>

                    <div className="ed-price__figure">
                        <span className="ed-price__currency">EUR</span>
                        <span className="ed-price__amount">1,199</span>
                        <span className="ed-price__footnote">
                            <em>directional</em>
                            <span>Final pricing may shift before launch.</span>
                        </span>
                    </div>

                    <div className="ed-price__rule" />

                    <dl className="ed-price__specs">
                        <div className="ed-price__spec">
                            <dt>Configuration</dt>
                            <dd>Single — no tiers, no upsell path.</dd>
                        </div>
                        <div className="ed-price__spec">
                            <dt>Core</dt>
                            <dd>Local-first AI control, orchestration, trust-boundary enforcement.</dd>
                        </div>
                        <div className="ed-price__spec">
                            <dt>Memory</dt>
                            <dd>16 GB unified working memory (directional).</dd>
                        </div>
                        <div className="ed-price__spec">
                            <dt>Status</dt>
                            <dd>Public direction — specifications may evolve before launch.</dd>
                        </div>
                    </dl>
                </div>
            </ContentSection>

            <ContentSection
                sectionId="what-reservation-means"
                eyebrow="What Reservation Means"
                title="The €1 deposit signals controlled early interest"
            >
                <CardGrid
                    pattern="meta"
                    columns={2}
                    items={[
                        {
                            title: 'What it is',
                            body: 'The €1 checkout is the live early-access mechanism. It places you in a controlled pre-production position ahead of a confirmed production run. It is a real commitment from both sides — yours and ours.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'What stage this belongs to',
                            body: 'AvenBox is a real working prototype currently being shaped into a production-grade product. The reservation is live, but it exists at an early stage — not at finished-product certainty.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="what-it-does-not-mean"
                eyebrow="What Reservation Does Not Mean"
                title="Clarity on what you are and are not committing to"
            >
                <CardGrid
                    pattern="meta"
                    columns={2}
                    items={[
                        {
                            title: 'Not a finished-product purchase',
                            body: 'The deposit is not proof of final specifications, final pricing, or a finished consumer platform. Engineering decisions will continue to evolve before production.',
                        },
                        {
                            title: 'Not a fixed delivery date',
                            body: `The ${ROADMAP_TARGET} target is the current public direction. It is a planning signal, not a contractual delivery commitment.`,
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="timeline"
                eyebrow="Timeline"
                title={`Current target: ${ROADMAP_TARGET}`}
                intro="Timing language on this site points to one consistent target. No mixed signals, no older dates."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL,
                            title: `${ROADMAP_TARGET} — public launch direction`,
                            body: `All public roadmap and launch language on this site is aligned to ${ROADMAP_TARGET}. That is the current engineering and product direction.`,
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'Current status: real working prototype',
                            body: 'AvenBox has a working local AI backend, privacy layer, orchestration core, and prototype interaction flows. It is not yet a finished, production-grade consumer product.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'Later: full platform maturity',
                            body: 'Finished hardware design, polished mobile experience, broader ecosystem compatibility, and consumer-grade reliability are roadmap goals beyond the current launch window.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="expectations"
                eyebrow="What to Expect"
                title="Serious interest deserves a serious level of honesty"
            >
                <TruthStateGrid
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'Reservation flow',
                            description:
                                'The €1 checkout is real and live. It is an early-stage mechanism, not a mature commercial rollout. You are reserving a position, not purchasing a finished product.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ESTIMATED_DIRECTIONAL,
                            title: 'Public pricing and timing',
                            description:
                                `EUR 1,199 and the ${ROADMAP_TARGET} target are directional. They reflect current planning and may shift as component costs (notably NAND and memory) settle before production.`,
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'Production maturity',
                            description:
                                'Full hardware finalization, platform polish, and consumer-grade launch readiness are ahead of the current prototype stage.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="questions"
                eyebrow="Questions"
                title="Read the supporting pages before reserving"
            >
                <LinkRow
                    links={[
                        { label: 'About', to: '/about' },
                        { label: 'Technology', to: '/technology' },
                        { label: 'FAQ', to: '/faq' },
                        { label: 'Contact', to: '/contact' },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="reserve"
                eyebrow="Reserve"
                title="If the direction is right for you, the next step is simple"
            >
                <div className="cta-row">
                    <a href="/contact" className="btn-pill btn-primary">
                        {reserveLabel}
                    </a>
                    <a href="/contact" className="btn-pill btn-secondary">
                        Join Waitlist
                    </a>
                </div>
            </ContentSection>
        </PageFrame>
    );
};

export default PricingPage;
